import { useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import { ActionDialog, type ActionField } from '@/components/hr/ActionDialog';
import {
  DialogSubject,
  ErrorNote,
  Field,
  Modal,
  Select,
  TextArea,
  errorMessage,
  money,
  shortDate,
  todayIso,
} from '@/components/ui';
import {
  LEFT_REASON_TEXT,
  useFarmOptions,
  useHrLookups,
  useShedOptions,
  type EmployeeRow,
  type PayRevision,
} from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import type { RecurringItem } from './EmployeeTabs';
import { fixedItemComponents } from './employeeRules';

export type EmployeeDialogState =
  | { kind: 'transfer' | 'suspend' | 'reinstate' | 'left' | 'rehire' | 'addItem'; row: EmployeeRow }
  | { kind: 'approve' | 'reject'; row: EmployeeRow; revision: PayRevision }
  | { kind: 'changeItem' | 'endItem'; row: EmployeeRow; item: RecurringItem };

/** What happened, and — after Mark as left — whose settlement to offer. */
export interface DialogOutcome {
  message: string;
  settleFor?: string;
}

function revisionPay(r: PayRevision): string {
  if (r.dailyRate) return `${money(r.dailyRate)} a day`;
  if (r.monthlyGross) return `${money(r.monthlyGross)} a month`;
  return 'new pay';
}

/** The person each of these dialogs acts on, with where they work now. */
function PersonSubject({
  row,
  figure,
  note,
}: {
  row: EmployeeRow;
  figure?: { label: string; value: ReactNode; unit?: string };
  note?: ReactNode;
}) {
  return (
    <DialogSubject
      icon="employee"
      code={row.code}
      name={row.nameEn}
      nameUr={row.nameUr}
      tags={[
        row.designation ? { icon: 'designation', label: row.designation } : null,
        {
          icon: 'farm',
          label: row.farmCode ? `${row.farmCode}${row.shedName ? ` · ${row.shedName}` : ''}` : 'Head office',
        },
      ]}
      {...(figure ? { figure } : {})}
      note={note}
    />
  );
}

/** The dialog behind each action that needs a little more than a click. */
export function EmployeeDialog({
  dialog,
  onClose,
  onDone,
}: {
  dialog: EmployeeDialogState;
  onClose: () => void;
  onDone: (outcome: DialogOutcome) => void;
}) {
  const allFarms = useAuth((s) => s.user?.scope.allFarms ?? false);
  const farms = useFarmOptions();
  const lookups = useHrLookups(dialog.kind === 'addItem');
  const { row } = dialog;
  const base = `/api/v1/hr/employees/${row.id}`;
  const who = `${row.code} ${row.nameEn}`;
  const today = todayIso();

  if (dialog.kind === 'transfer') {
    return <TransferDialog row={row} onClose={onClose} onDone={onDone} />;
  }

  const farmChoices = [
    ...(allFarms ? [{ value: 'HEAD_OFFICE', label: 'Head office' }] : []),
    ...(farms.data ?? []).map((f) => ({ value: f.id, label: `${f.code} · ${f.nameEn}` })),
  ];

  type Config = {
    title: string;
    icon: IconKind;
    intro?: string;
    fields: ActionField[];
    initial?: Record<string, string>;
    submit: string;
    danger?: boolean;
    run: (v: Record<string, string>) => Promise<unknown>;
    outcome: DialogOutcome;
  };

  let c: Config;
  switch (dialog.kind) {
    case 'suspend':
      c = {
        title: 'Suspend',
        icon: 'suspend',
        intro: 'Record that they are suspended from a date. Reinstate them when they come back.',
        fields: [
          { key: 'date', label: 'Suspended from', type: 'date', required: true },
          { key: 'note', label: 'Why', type: 'textarea' },
        ],
        initial: { date: today },
        submit: 'Suspend',
        danger: true,
        run: (v) => api.post(`${base}/suspend`, { date: v.date, note: v.note?.trim() || undefined }),
        outcome: { message: `${who} is suspended.` },
      };
      break;
    case 'reinstate':
      c = {
        title: 'Reinstate',
        icon: 'reactivate',
        fields: [
          { key: 'date', label: 'Back at work from', type: 'date', required: true },
          { key: 'note', label: 'Note', type: 'textarea' },
        ],
        initial: { date: today },
        submit: 'Reinstate',
        run: (v) => api.post(`${base}/reinstate`, { date: v.date, note: v.note?.trim() || undefined }),
        outcome: { message: `${who} is back at work.` },
      };
      break;
    case 'left':
      c = {
        title: 'Mark as left',
        icon: 'leaver',
        intro: 'Give their last day of work. A final settlement follows: pay still due, and anything they still owe.',
        fields: [
          { key: 'leftOn', label: 'Last day of work', type: 'date', required: true },
          {
            key: 'reason',
            label: 'Why they left',
            type: 'select',
            required: true,
            options: Object.entries(LEFT_REASON_TEXT).map(([value, label]) => ({ value, label })),
          },
          { key: 'note', label: 'Note', type: 'textarea' },
        ],
        initial: { leftOn: today },
        submit: 'Mark as left',
        danger: true,
        run: (v) => api.post(`${base}/left`, { leftOn: v.leftOn, reason: v.reason, note: v.note?.trim() || undefined }),
        outcome: { message: `${who} is marked as left. Their final settlement comes next.`, settleFor: row.id },
      };
      break;
    case 'rehire':
      c = {
        title: 'Take back on',
        icon: 'joiner',
        intro: 'Their pay stays as it was. Use Change pay afterwards if it is different now.',
        fields: [
          { key: 'joinedOn', label: 'New joining date', type: 'date', required: true },
          {
            key: 'farm',
            label: 'Farm',
            type: 'select',
            required: true,
            options: [{ value: 'SAME', label: 'Same as before' }, ...farmChoices],
          },
          { key: 'note', label: 'Note', type: 'textarea' },
        ],
        initial: { joinedOn: today, farm: 'SAME' },
        submit: 'Take back on',
        run: (v) =>
          api.post(`${base}/rehire`, {
            joinedOn: v.joinedOn,
            ...(v.farm === 'SAME' ? {} : { farmId: v.farm === 'HEAD_OFFICE' ? null : v.farm }),
            note: v.note?.trim() || undefined,
          }),
        outcome: { message: `${who} is working again.` },
      };
      break;
    case 'approve':
      c = {
        title: `Approve ${dialog.revision.documentNo}`,
        icon: 'approve',
        intro: `From ${shortDate(dialog.revision.effectiveFrom)}. Why: ${dialog.revision.reason}`,
        fields: [{ key: 'comment', label: 'Comment', type: 'textarea' }],
        submit: 'Approve',
        run: (v) => api.post(`/api/v1/hr/pay-changes/${dialog.revision.id}/approve`, { comment: v.comment?.trim() || undefined }),
        outcome: { message: `${dialog.revision.documentNo} approved. The new pay applies from ${shortDate(dialog.revision.effectiveFrom)}.` },
      };
      break;
    case 'reject':
      c = {
        title: `Reject ${dialog.revision.documentNo}`,
        icon: 'reject',
        intro: `From ${shortDate(dialog.revision.effectiveFrom)}.`,
        fields: [{ key: 'comment', label: 'Why', type: 'textarea', required: true }],
        submit: 'Reject',
        danger: true,
        run: (v) => api.post(`/api/v1/hr/pay-changes/${dialog.revision.id}/reject`, { comment: v.comment?.trim() }),
        outcome: { message: `${dialog.revision.documentNo} rejected. Their pay stays as it was.` },
      };
      break;
    case 'addItem':
      c = {
        title: 'Add a fixed monthly item',
        icon: 'add',
        intro: 'An allowance or deduction that repeats every month between these dates.',
        fields: [
          {
            key: 'componentId',
            label: 'Item',
            type: 'select',
            required: true,
            options: fixedItemComponents(lookups.data?.payComponents).map((p) => ({
              value: p.id,
              label: `${p.nameEn} (${p.kind === 'DEDUCTION' ? 'deduction' : 'allowance'})`,
            })),
          },
          { key: 'amount', label: 'Amount each month', type: 'number', required: true },
          { key: 'fromDate', label: 'From', type: 'date', required: true },
          { key: 'toDate', label: 'Until', type: 'date', hint: 'Leave empty if it has no end' },
          { key: 'note', label: 'Note', type: 'textarea' },
        ],
        initial: { fromDate: `${today.slice(0, 7)}-01` },
        submit: 'Add',
        run: (v) =>
          api.post(`${base}/fixed-items`, {
            componentId: v.componentId,
            amount: v.amount?.trim(),
            fromDate: v.fromDate,
            toDate: v.toDate || null,
            note: v.note?.trim() || null,
          }),
        outcome: { message: 'Fixed monthly item added.' },
      };
      break;
    case 'changeItem':
      c = {
        title: `Change ${dialog.item.component}`,
        icon: 'edit',
        fields: [
          { key: 'amount', label: 'Amount each month', type: 'number', hint: dialog.item.payAmount ? undefined : 'Leave empty to keep it as it is' },
          { key: 'toDate', label: 'Until', type: 'date', hint: 'Leave empty if it has no end' },
          { key: 'note', label: 'Note', type: 'textarea' },
        ],
        initial: {
          amount: dialog.item.payAmount ? String(Number(dialog.item.payAmount)) : '',
          toDate: dialog.item.toDate ?? '',
          note: dialog.item.note ?? '',
        },
        submit: 'Save',
        run: (v) =>
          api.patch(`/api/v1/hr/fixed-items/${dialog.item.id}`, {
            ...(v.amount?.trim() ? { amount: v.amount.trim() } : {}),
            toDate: v.toDate || null,
            note: v.note?.trim() || null,
          }),
        outcome: { message: `${dialog.item.component} saved.` },
      };
      break;
    case 'endItem':
      c = {
        title: `End ${dialog.item.component}`,
        icon: 'retire',
        intro: 'Payroll stops adding it after this date. What was already paid stays as it is.',
        fields: [{ key: 'toDate', label: 'Last date', type: 'date', required: true }],
        initial: { toDate: today },
        submit: 'End it',
        run: (v) => api.patch(`/api/v1/hr/fixed-items/${dialog.item.id}`, { toDate: v.toDate }),
        outcome: { message: `${dialog.item.component} ends on ${shortDate(today)}.` },
      };
      break;
  }

  return (
    <ActionDialog
      title={c.title}
      icon={c.icon}
      subject={
        <PersonSubject
          row={row}
          {...(dialog.kind === 'approve' || dialog.kind === 'reject'
            ? { figure: { label: 'New pay', value: revisionPay(dialog.revision) } }
            : {})}
        />
      }
      intro={c.intro}
      fields={c.fields}
      {...(c.initial ? { initial: c.initial } : {})}
      submitLabel={c.submit}
      danger={c.danger ?? false}
      onClose={onClose}
      onSubmit={async (v) => {
        await c.run(v);
        onDone(dialog.kind === 'endItem' ? { message: `${dialog.item.component} ends on ${shortDate(v.toDate)}.` } : c.outcome);
      }}
    />
  );
}

/** Move to another farm or shed, or to head office, from a date. */
function TransferDialog({
  row,
  onClose,
  onDone,
}: {
  row: EmployeeRow;
  onClose: () => void;
  onDone: (outcome: DialogOutcome) => void;
}) {
  const allFarms = useAuth((s) => s.user?.scope.allFarms ?? false);
  const farms = useFarmOptions();
  const [farmId, setFarmId] = useState(row.farmId ?? '');
  const [shedId, setShedId] = useState(row.shedId ?? '');
  const [from, setFrom] = useState(todayIso());
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const sheds = useShedOptions(farmId || null);
  const title = 'Move to another farm';

  async function submit() {
    if (!farmId && !allFarms) return setProblem('Choose the farm.');
    if (farmId === (row.farmId ?? '') && shedId === (row.shedId ?? '')) return setProblem('That is where they already work.');
    if (!from) return setProblem('Choose the date the move starts.');
    if (from > todayIso()) return setProblem('A move can start today or earlier, not in the future.');
    setProblem(null);
    setPending(true);
    try {
      await api.post(`/api/v1/hr/employees/${row.id}/transfer`, {
        farmId: farmId || null,
        shedId: farmId && shedId ? shedId : null,
        effectiveFrom: from,
        note: note.trim() || undefined,
      });
      const to = farmId ? (farms.data ?? []).find((f) => f.id === farmId)?.code ?? 'the new farm' : 'head office';
      onDone({ message: `${row.code} ${row.nameEn} moved to ${to} from ${shortDate(from)}.` });
      onClose();
    } catch (e) {
      setProblem(errorMessage(e, 'Could not move them'));
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose} medium>
      <h2 className="withicon">
        <AppIcon kind="moveFarm" />
        {title}
      </h2>
      <PersonSubject row={row} />
      <p className="muted">Attendance and labour cost follow them from the date you give.</p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="formgrid formgrid--2">
          <Select
            label="Move to farm"
            required={!allFarms}
            value={farmId}
            onChange={(v) => {
              setFarmId(v);
              setShedId('');
            }}
            placeholder={allFarms ? 'Head office' : undefined}
            options={(farms.data ?? []).map((f) => ({ value: f.id, label: `${f.code} · ${f.nameEn}` }))}
          />
          <Select
            label="Shed"
            value={shedId}
            onChange={setShedId}
            disabled={!farmId}
            placeholder={farmId ? 'Not tied to one shed' : undefined}
            prompt="Choose a farm first"
            options={(sheds.data ?? []).map((s) => ({ value: s.id, label: `${s.code} · ${s.nameEn}` }))}
          />
          <Field label="From" type="date" required value={from} onChange={setFrom} />
          <TextArea label="Note" value={note} onChange={setNote} rows={2} />
        </div>
        {problem && <ErrorNote message={problem} />}
        <div className="formactions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <span className="formactions__spacer" />
          <button type="submit" className="btn btn--primary" disabled={pending}>
            {pending ? 'Working…' : 'Move'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
